import { useEffect, useRef, useState, useCallback } from "react";
import { gpsToWorld } from "@/lib/gps";

const STORAGE_KEY = "hln_gps_enabled";

/**
 * Watches the browser's Geolocation API and returns the visitor's position
 * mapped to app world (x, z) coordinates. Auto-remembers the user's opt-in
 * in localStorage so we don't nag them on every page load.
 *
 * Returns:
 *   supported        — bool (device has geolocation)
 *   enabled          — bool (visitor opted in AND permission granted)
 *   worldPos         — { x, z } | null  (null when off-site or not yet fixed)
 *   accuracyMeters   — number | null    (last GPS accuracy, for UI)
 *   error            — string | null    (last error message)
 *   enable()         — start watching
 *   disable()        — stop + forget
 */
export function useVisitorGPS() {
  const supported = typeof window !== "undefined" && "geolocation" in navigator;
  const [enabled, setEnabled] = useState(false);
  const [worldPos, setWorldPos] = useState(null);
  const [accuracyMeters, setAccuracyMeters] = useState(null);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(null);

  const onSuccess = useCallback((pos) => {
    setError(null);
    setAccuracyMeters(pos.coords.accuracy);
    setWorldPos(gpsToWorld(pos.coords.latitude, pos.coords.longitude));
  }, []);

  const onError = useCallback((err) => {
    setError(err.message || "Location error");
    setWorldPos(null);
  }, []);

  const startWatch = useCallback(() => {
    if (!supported) return;
    if (watchIdRef.current !== null) return;
    watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    });
  }, [supported, onSuccess, onError]);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const enable = useCallback(() => {
    if (!supported) return;
    setEnabled(true);
    localStorage.setItem(STORAGE_KEY, "1");
    startWatch();
  }, [supported, startWatch]);

  const disable = useCallback(() => {
    setEnabled(false);
    setWorldPos(null);
    setAccuracyMeters(null);
    localStorage.removeItem(STORAGE_KEY);
    stopWatch();
  }, [stopWatch]);

  // Auto-resume on mount if the visitor previously opted in.
  useEffect(() => {
    if (!supported) return undefined;
    if (localStorage.getItem(STORAGE_KEY) === "1") {
      setEnabled(true);
      startWatch();
    }
    return stopWatch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  return { supported, enabled, worldPos, accuracyMeters, error, enable, disable };
}
